<template>
  <div class="vocabulary-autocomplete">
    <input 
      type="text" 
      v-model="searchValue"
      @input="handleInput"
      @keydown="handleKeydown"
      @focus="handleFocus"
      @blur="handleBlur"
      :placeholder="placeholder"
      ref="inputRef"
    />
    
    <div 
      v-if="showDropdown" 
      class="dropdown-list"
    >
      <div 
        v-if="isLoading"
        class="dropdown-message"
      >
        검색 중...
      </div>
      
      <template v-else>
        <div 
          v-if="suggestions.length === 0 && hasSearched && searchValue.trim()"
          class="dropdown-no-result"
        >
          <div class="no-result-message">검색 결과가 없습니다</div>
          <button 
            type="button" 
            class="btn-create"
            @mousedown.prevent="openCreateModal"
          >
            생성하기
          </button>
        </div>
        
        <div 
          v-else
          v-for="(item, index) in suggestions" 
          :key="item.id"
          class="dropdown-item"
          :class="{ 'active': index === selectedIndex }"
          @mousedown.prevent="selectItem(item)"
          @mouseenter="selectedIndex = index"
        >
          <div class="item-code">{{ item.code }}</div>
          <div class="item-system">
            <span v-if="item.system1">{{ item.system1 }}</span>
            <span v-if="item.system1 && item.system2"> > </span>
            <span v-if="item.system2">{{ item.system2 }}</span>
          </div>
          <div v-if="item.description" class="item-desc">{{ item.description }}</div>
        </div>
      </template>
    </div>
    
    <!-- 생성 모달 -->
    <div v-if="showCreateModal" class="modal-overlay">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>어휘 생성</h3>
          <button type="button" class="btn-close" @click="closeCreateModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>어휘코드 *</label>
            <input 
              type="text" 
              v-model="createForm.code"
              placeholder="어휘코드를 입력하세요"
            />
          </div>
          <div class="form-group">
            <label>체계1</label>
            <input 
              type="text" 
              v-model="createForm.system1"
              placeholder="체계1을 입력하세요"
            />
          </div>
          <div class="form-group">
            <label>체계2</label>
            <input 
              type="text" 
              v-model="createForm.system2"
            />
          </div>
          <div class="form-group">
            <label>설명</label>
            <textarea 
              v-model="createForm.description"
              placeholder="설명을 입력하세요"
              rows="3"
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn-cancel" @click="closeCreateModal">취소</button>
          <button type="button" class="btn-submit" @click="createVocabulary">생성</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {createNamespacedHelpers} from "vuex";
const Hub = createNamespacedHelpers('Hub');
export default {
  name: 'VocabularyAutocomplete',
  props: {
    placeholder: {
      type: String,
      default: '어휘번호를 입력하세요'
    },
    debounceTime: {
      type: Number,
      default: 300
    },
    selectedVocabularyId: {
      type: String,
      default: null,
    }
  },
  emits: ['select'],
  data() {
    return {
      searchValue: '',
      suggestions: [],
      showDropdown: false,
      selectedIndex: -1,
      debounceTimer: null,
      isLoading: false,
      hasSearched: false,
      showCreateModal: false,
      createForm: {
        code: '',
        system1: '',
        system2: '',
        description: ''
      }
    }
  },
  computed:{
    ...Hub.mapGetters([
      'getVocabularyList'
    ]),
  },
  watch:{
    selectedVocabularyId:{
      deep: true,
      handler(value){
        if(value == null || !value){
          return null;
        }else{
          const data = this.getVocabularyList.find(c=>c?.id==Number(value));
          this.searchValue = data?.code;
          this.showDropdown = false;
          this.selectedIndex = -1;
        }
      }
    }
  },
  methods: {
    ...Hub.mapActions(
        [
          "fetchVocabularyList",
        ]
    ),
    async handleInput() {
      const value = this.searchValue;
      
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      this.debounceTimer = setTimeout(async () => {
        this.isLoading = true;
        this.showDropdown = true;
        this.hasSearched = false;
        
        try {
          const res = await this.$wApi.property.getPropertyVocabularyList({code: value});
          this.suggestions = res || [];
          this.hasSearched = true;
          this.selectedIndex = -1;
        } catch (error) {
          console.error('Error fetching vocabulary:', error);
          this.suggestions = [];
          this.hasSearched = true;
        } finally {
          this.isLoading = false;
        }
      }, this.debounceTime);
    },
    
    handleFocus() {
      this.handleInput();
    },
    
    resetData(){
      this.searchValue= '';
      this.suggestions= [];
      this.showDropdown= false;
      this.selectedIndex= -1;
      this.debounceTimer= null;
      this.isLoading= false;
      this.hasSearched= false;
      this.showCreateModal= false;
      this.createForm= {
        code: '',
        system1: '',
        system2: '',
        description: ''
      };
    },
    
    handleKeydown(event) {
      if (!this.showDropdown || (this.suggestions.length === 0 && !this.hasSearched) || this.isLoading) {
        return;
      }
      
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.selectedIndex = Math.min(
            this.selectedIndex + 1, 
            this.suggestions.length - 1
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
          break;
        case 'Enter':
          event.preventDefault();
          if (this.selectedIndex >= 0 && this.suggestions.length > 0) {
            this.selectItem(this.suggestions[this.selectedIndex]);
          } else if (this.suggestions.length === 0 && this.hasSearched && this.searchValue.trim()) {
            this.openCreateModal();
          }
          break;
        case 'Escape':
          this.showDropdown = false;
          this.selectedIndex = -1;
          break;
      }
    },
    
    selectItem(item) {
      this.searchValue = item?.code;
      this.$emit('select', item);
      this.showDropdown = false;
      this.selectedIndex = -1;
    },
    
    handleBlur() {
      if(this.showCreateModal) return;
      setTimeout(() => {
        // 드롭다운이 열려있고 검색 결과가 있을 때
        if (this.showDropdown && this.suggestions.length > 0 && this.searchValue.trim() != "") {
          // 입력값과 정확히 일치하는 항목 찾기
          const exactMatch = this.suggestions.find(
            item => item.code.toLowerCase() === this.searchValue.trim().toLowerCase()
          );
          
          if (exactMatch) {
            // 정확히 일치하는 항목이 있으면 자동 선택
            this.selectItem(exactMatch);
          } else {
            // 일치하는 항목이 없으면 입력값 비우기
            this.searchValue = '';
            this.selectItem(null); // 외부도 빈값으로 update
            this.resetData();
          }
        } else if (this.searchValue.trim() != "" && this.hasSearched && this.suggestions.length === 0) {
          // 검색했지만 결과가 없는 경우에도 입력값 비우기
          this.searchValue = '';
          this.selectItem(null); // 외부도 빈값으로 update
          this.resetData();
        }else if (this.searchValue.trim() == ""){
          // 검색어를 모두 지우고 나간 경우
          this.searchValue = '';
          this.selectItem(null); // 외부도 빈값으로 update
          this.resetData();
        }
        
        this.showDropdown = false;
        this.selectedIndex = -1;
      }, 200);
    },
    
    openCreateModal() {
      this.createForm = {
        code: this.searchValue,
        system1: '',
        system2: '',
        description: ''
      };
      this.showCreateModal = true;
      this.showDropdown = false;
    },
    
    closeCreateModal() {
      this.showCreateModal = false;
      this.createForm = {
        code: '',
        system1: '',
        system2: '',
        description: ''
      };
    },
    
    async createVocabulary() {
      if (!this.createForm.code || !this.createForm.code.trim()) {
        alert('코드를 입력해주세요.');
        return;
      }
      
      try {
        // API 호출로 어휘 생성
        const res = await this.$wApi.property.createPropertyVocabulary(this.createForm);
        
        if (res && !res.error) {
          alert('생성되었습니다.');
          await this.fetchVocabularyList(); // 최신화
          // 생성된 어휘 자동 선택
          const createdItem = {
            id: res.id,
            code: this.createForm.code,
            system1: this.createForm.system1,
            system2: this.createForm.system2,
            description: this.createForm.description
          };
          this.selectItem(createdItem);
          this.closeCreateModal();
        } else {
          alert(res.message || '생성에 실패했습니다.');
        }
      } catch (error) {
        console.error('Error creating vocabulary:', error);
        alert('생성 중 오류가 발생했습니다.');
      }
    }
  },
  beforeUnmount() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
}
</script>

<style scoped>
.vocabulary-autocomplete {
  position: relative;
  width: 100%;
}

.vocabulary-autocomplete input[type="text"] {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.vocabulary-autocomplete input[type="text"]:focus {
  outline: none;
  border-color: #4CAF50;
}

.dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  overflow-x: auto;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.dropdown-message {
  padding: 12px;
  text-align: center;
  color: #666;
  font-size: 14px;
}

.dropdown-no-result {
  padding: 12px;
  text-align: center;
}

.no-result-message {
  color: #999;
  font-size: 14px;
  margin-bottom: 10px;
}

.btn-create {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.btn-create:hover {
  background-color: #45a049;
}

.dropdown-item {
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover,
.dropdown-item.active {
  background-color: #f5f5f5;
}

.item-code {
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.item-system {
  font-size: 13px;
  color: #555;
  margin-top: 2px;
}

.item-desc {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.dropdown-list::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

.dropdown-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.dropdown-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 2px;
}

.dropdown-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* 모달 스타일 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.btn-close {
  background: none;
  border: none;
  font-size: 28px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  line-height: 1;
}

.btn-close:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #4CAF50;
}

.form-group textarea {
  resize: vertical;
  font-family: inherit;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #ddd;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-cancel,
.btn-submit {
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-cancel {
  background-color: #f5f5f5;
  color: #333;
}

.btn-cancel:hover {
  background-color: #e0e0e0;
}

.btn-submit {
  background-color: #4CAF50;
  color: white;
}

.btn-submit:hover {
  background-color: #45a049;
}
</style>